'use client';

import { c as _c } from "react/compiler-runtime";
import { formatPremiumDuration, type Product } from '@shared/all/domains/products';
import { formatUSD } from '@shared/utils/helpers/formatCurrency';
import toast from '@shared/utils/helpers/toast';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import React, { useState } from 'react';
import useDarkMode from '../../hooks/useDarkMode';
import { client } from '../../utils/trpc';
import { useAppModals } from '../../widgets/WidgetsContext';
import Modal, { ModalContent, ModalHeader } from '../HOC/Modal';
import Button from './Button';
import FoundersTokensTermsModal from './FoundersTokensTermsModal';
type Props = {
  product: Product;
  closeHandler: () => void;
};
const PurchaseModal = props => {
  const $ = _c(40);
  if (!process.env.NEXT_PUBLIC_STRIPE_PUB_KEY) {
    throw new Error("Stripe public key is missing");
  }
  const {
    isDarkMode
  } = useDarkMode();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUB_KEY);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const stripePromise = t0;
  const {
    displayPrice
  } = props.product;
  const isSubscription = props.product.type === "MONTHLY_SUBSCRIPTION";
  let t1;
  if ($[1] !== props) {
    t1 = () => props.closeHandler();
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== props) {
    t2 = () => props.closeHandler();
    $[3] = props;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== props.product.title) {
    t3 = <h2>{props.product.title}</h2>;
    $[5] = props.product.title;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <ModalHeader closeHandler={t2}>{t3}</ModalHeader>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== props.product.description) {
    t5 = <p className="text-base text-textSecondary">{props.product.description}</p>;
    $[10] = props.product.description;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== props.product.bonusTrons || $[13] !== props.product.tron || $[14] !== props.product.type) {
    t6 = props.product.type === "TRON" && props.product.tron && <p className="text-base font-emphasis text-textPrimary">{(Number(props.product.tron.quantity) + (props.product.bonusTrons ?? 0)).toLocaleString()}{" "}trons{(props.product.tron.premiumDays ?? 0) > 0 && ` · ${formatPremiumDuration(props.product.tron.premiumDays ?? 0)}`}</p>;
    $[12] = props.product.bonusTrons;
    $[13] = props.product.tron;
    $[14] = props.product.type;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <p className="text-base text-textSecondary">Enter your credit card info to proceed. Payments are processed by Stripe.</p>;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = isSubscription ? "subscription" : "payment";
  const t9 = isDarkMode ? "night" : "stripe";
  let t10;
  if ($[17] !== t9) {
    t10 = {
      theme: t9
    };
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== displayPrice || $[20] !== t10 || $[21] !== t8) {
    t11 = {
      mode: t8,
      amount: displayPrice,
      currency: "usd",
      appearance: t10
    };
    $[19] = displayPrice;
    $[20] = t10;
    $[21] = t8;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== displayPrice) {
    t12 = formatUSD(displayPrice);
    $[23] = displayPrice;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== props.closeHandler || $[26] !== props.product || $[27] !== t12) {
    t13 = <CheckoutForm total={t12} closeHandler={props.closeHandler} product={props.product} />;
    $[25] = props.closeHandler;
    $[26] = props.product;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t11 || $[30] !== t13) {
    t14 = <Elements options={t11} stripe={stripePromise}>{t13}</Elements>;
    $[29] = t11;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t14 || $[33] !== t5 || $[34] !== t6) {
    t15 = <ModalContent>{t5}{t6}{t7}{t14}</ModalContent>;
    $[32] = t14;
    $[33] = t5;
    $[34] = t6;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t1 || $[37] !== t15 || $[38] !== t4) {
    t16 = <Modal closeHandler={t1}>{t4}{t15}</Modal>;
    $[36] = t1;
    $[37] = t15;
    $[38] = t4;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  return t16;
};
const CheckoutForm = ({
  total,
  closeHandler,
  product
}: {
  total: string;
  closeHandler: Function;
  product: Product;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);
  const [foundersTokensTermsAccepted, setFoundersTokensTermsAccepted] = useState(false);
  const [showFoundersTokensTerms, setShowFoundersTokensTerms] = useState(false);
  // Starts FALSE and is never pre-ticked. Card-network rules require the buyer
  // to agree before a card may be charged while they are not present, so this
  // has to be an action they took, not a default they failed to notice.
  const [saveCardForAutoTopUp, setSaveCardForAutoTopUp] = useState(false);
  const {
    throwConfetti
  } = useAppModals();
  const isFoundersToken = product.type === 'FOUNDERS_TOKEN';
  // Auto top-up only ever buys tron packages, so the consent has nowhere to
  // apply on any other product. Stripe-only by nature: this modal is the Stripe
  // path, and IAP has no off-session recharge primitive to consent to.
  const isTronPackage = product.type === 'TRON';
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (isFoundersToken && !foundersTokensTermsAccepted) {
      toast('Please accept the Founders Tokens Terms of Service to continue', '', 'error');
      return;
    }
    setIsLoading(true);
    const {
      error: submitError
    } = await elements.submit();
    if (submitError) {
      toast(submitError.message || 'Please check your payment details', '', 'error');
      setIsLoading(false);
      return;
    }
    try {
      // Use server-computed displayPrice — matches what server expects in checkPurchase
      const [error_0, result] = await client.products.checkPurchase.mutate({
        productId: product.id.toString(),
        type: 'STRIPE',
        receivedPrice: product.displayPrice,
        app: 'factiii',
        // Sets `setup_future_usage: 'off_session'` on the intent, which is what
        // makes the card reusable; the webhook then stores it on the user.
        saveCardForAutoTopUp
      });
      if (error_0) return setIsLoading(false);
      if (!result.secret) throw new Error('Failed to get payment details');
      const {
        error: confirmError
      } = await stripe.confirmPayment({
        elements,
        clientSecret: result.secret,
        confirmParams: {
          return_url: `${process.env.NEXT_PUBLIC_SITE_URL}/products/my`
        },
        redirect: 'if_required'
      });
      if (confirmError) {
        const message_0 = confirmError.message || 'Something went wrong while processing your payment';
        toast(confirmError.type === 'card_error' || confirmError.type === 'validation_error' ? message_0 : 'Something went wrong while processing your payment', '', 'error');
      } else {
        throwConfetti();
        closeHandler();
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Payment failed';
      toast(message, '', 'error');
    }
    setIsLoading(false);
  };
  return <>
      <form id="payment-form" className="space-y-4" onSubmit={handleSubmit}>
        <PaymentElement id="payment-element" options={{
        layout: 'tabs'
      }} onReady={() => setIsPaymentElementReady(true)} />

        {/* Founders Token Terms of Service Checkbox */}
        {isFoundersToken && <div className="space-y-3 rounded-lg border border-ds-border-standard bg-background-secondary p-4">
            <div className="flex items-start space-x-3">
              <input type="checkbox" id="foundersTokensTerms" checked={foundersTokensTermsAccepted} onChange={e_0 => setFoundersTokensTermsAccepted(e_0.target.checked)} className="mt-1 size-4 rounded border-ds-border-standard text-primary focus:ring-primary" />
              <label htmlFor="foundersTokensTerms" className="cursor-pointer text-sm text-textSecondary">
                I have read and agree to the{' '}
                <button type="button" onClick={() => setShowFoundersTokensTerms(true)} className="text-primary underline hover:text-primary/80">
                  Founders Tokens Terms of Service
                </button>
                , including the non-transferable nature of tokens and anti-abuse
                policies.
              </label>
            </div>
            {!foundersTokensTermsAccepted && <p className="text-xs text-textSecondary">
                * You must accept the Founders Tokens Terms of Service to
                complete your purchase.
              </p>}
          </div>}

        {/* The one place a card can be kept for auto top-up. Without this the
            setting in Settings > Billing can never be switched on, because
            there is no card it is allowed to charge. */}
        {isTronPackage && <div className="flex items-start space-x-3 rounded-lg border border-ds-border-standard bg-background-secondary p-4">
            <input type="checkbox" id="saveCardForAutoTopUp" checked={saveCardForAutoTopUp} onChange={e_1 => setSaveCardForAutoTopUp(e_1.target.checked)} className="mt-1 size-4 rounded border-ds-border-standard text-primary focus:ring-primary" />
            <label htmlFor="saveCardForAutoTopUp" className="cursor-pointer text-sm text-textSecondary">
              <span className="font-emphasis text-textPrimary">
                Save my card for auto top-up
              </span>
              <span className="mt-1 block">
                Lets us charge this card when your trons run low, so a long task
                never stops halfway. You set the amount and your spending limits
                in Settings &rarr; Billing, and you can turn it off there at any
                time.
              </span>
            </label>
          </div>}

        <p className="text-start font-semibold">Total: {total}</p>
        <Button type="submit" text="Pay" loading={isLoading} classNames="w-full" disabled={isLoading || !stripe || !elements || !isPaymentElementReady || isFoundersToken && !foundersTokensTermsAccepted} />
      </form>

      {showFoundersTokensTerms && <FoundersTokensTermsModal closeHandler={() => setShowFoundersTokensTerms(false)} />}
    </>;
};
export default PurchaseModal;